/**
 * <s-ambient-video> — Muted, looping background video with an accessible pause control.
 *
 * - Visitors who prefer reduced motion never see playback: the video is paused,
 *   hidden, and the poster underneath stays visible.
 * - Playback pauses while the element is off-screen to save battery and data,
 *   and resumes when it returns, unless the visitor paused it.
 *
 * Commands (via commandfor/command on the descendant [data-toggle] button):
 *   --toggle — Pause or resume playback.
 */
class SAmbientVideo extends HTMLElement {
  #video;
  #button;
  #observer;
  #userPaused = false;
  #ac;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#button = this.querySelector('[data-toggle]');
    if (!this.#video) return;

    this.#ac = new AbortController();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (reduceMotion.matches) {
      this.#userPaused = true;
      this.#video.removeAttribute('autoplay');
      this.#video.pause();
      this.#video.hidden = true;
      this.#sync();
    }

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle') this.#toggle();
    }, { signal: this.#ac.signal });

    // Fallback for browsers without Invoker Commands support.
    this.#button?.addEventListener('click', (event) => {
      if (!('command' in HTMLButtonElement.prototype)) {
        event.preventDefault();
        this.#toggle();
      }
    }, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      if (this.#userPaused) return;
      if (entry.isIntersecting) {
        this.#video.play().catch(() => {});
      } else {
        this.#video.pause();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #toggle() {
    if (this.#video.paused) {
      this.#userPaused = false;
      this.#video.hidden = false;
      this.#video.play().catch(() => {});
    } else {
      this.#userPaused = true;
      this.#video.pause();
    }
    this.#sync();
  }

  #sync() {
    if (!this.#button) return;
    const paused = this.#userPaused;
    this.#button.setAttribute('aria-label', paused ? this.dataset.labelPlay : this.dataset.labelPause);
    this.#button.querySelector('[data-icon-pause]')?.classList.toggle('hidden', paused);
    this.#button.querySelector('[data-icon-play]')?.classList.toggle('hidden', !paused);
  }
}

if (!customElements.get('s-ambient-video')) {
  customElements.define('s-ambient-video', SAmbientVideo);
}
